import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import {
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  XMarkIcon,
  ArrowPathIcon,
  PlusCircleIcon,
  ArrowUpCircleIcon,
  NoSymbolIcon,
  InformationCircleIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';

interface SyncLog {
  id: string;
  timestamp: string;
  logType: string;
  direction: string;
  externalTable?: string;
  externalSysId?: string;
  externalNumber?: string;
  caseId?: string;
  caseNumber?: string;
  reason: string;
  errorDetail?: string;
  retryable: boolean;
  retriedAt?: string;
  createdAt: string;
  syncRun?: {
    id: string;
    startedAt: string;
    status: string;
    triggerType: string;
  };
}

interface Stats {
  totalLogs: number;
  errors: number;
  failedLinks: number;
  lastRun: {
    id: string;
    startedAt: string;
    status: string;
    recordsCreated: number;
    recordsUpdated: number;
    recordsFailed: number;
  } | null;
}

interface SyncRun {
  id: string;
  startedAt: string;
  completedAt: string | null;
  status: string;
  triggerType: string;
  recordsSynced: number;
  recordsCreated: number;
  recordsUpdated: number;
  recordsSkipped: number;
  recordsFailed: number;
  _count?: { logs: number };
}

const logTypeConfig: Record<string, { label: string; color: string; icon: React.ComponentType<{ className?: string }>; iconColor: string; borderColor: string }> = {
  CREATE: {
    label: 'Created',
    color: 'bg-green-100 text-green-800 border-green-300',
    icon: PlusCircleIcon,
    iconColor: 'text-green-600',
    borderColor: '#22c55e',
  },
  UPDATE: {
    label: 'Updated',
    color: 'bg-blue-100 text-blue-800 border-blue-300',
    icon: ArrowUpCircleIcon,
    iconColor: 'text-blue-600',
    borderColor: '#3b82f6',
  },
  SKIP: {
    label: 'Skipped',
    color: 'bg-gray-100 text-gray-800 border-gray-300',
    icon: ExclamationTriangleIcon,
    iconColor: 'text-gray-600',
    borderColor: '#9ca3af',
  },
  ERROR: {
    label: 'Error',
    color: 'bg-red-100 text-red-800 border-red-300',
    icon: ExclamationCircleIcon,
    iconColor: 'text-red-600',
    borderColor: '#ef4444',
  },
  LOOP_PREVENTED: {
    label: 'Loop Prevented',
    color: 'bg-purple-100 text-purple-800 border-purple-300',
    icon: NoSymbolIcon,
    iconColor: 'text-purple-600',
    borderColor: '#a855f7',
  },
  RETRY: {
    label: 'Retried',
    color: 'bg-amber-100 text-amber-800 border-amber-300',
    icon: ArrowPathIcon,
    iconColor: 'text-amber-600',
    borderColor: '#f59e0b',
  },
  INFO: {
    label: 'Info',
    color: 'bg-slate-100 text-slate-800 border-slate-300',
    icon: InformationCircleIcon,
    iconColor: 'text-slate-600',
    borderColor: '#64748b',
  },
};

export interface ServiceNowSyncLogListProps {
  embedded?: boolean;
  customerId?: string;
}

const ServiceNowSyncLogList: React.FC<ServiceNowSyncLogListProps> = ({
  embedded = false,
  customerId,
}) => {
  const { formatDateTime } = useDateFormatter();
  const queryClient = useQueryClient();

  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [filterLogType, setFilterLogType] = useState('');
  const [filterDirection, setFilterDirection] = useState('');
  const [filterSyncRunId, setFilterSyncRunId] = useState('');
  const [retryableOnly, setRetryableOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const PAGE_SIZE_OPTIONS = [25, 50, 100];

  const { data: syncRunsData } = useQuery({
    queryKey: ['servicenow-sync-runs', customerId],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: '10' });
      if (customerId) params.set('customerId', customerId);
      const response = await api.get(`/integrations/servicenow/logs/runs?${params}`);
      return response.data.data as { runs: SyncRun[]; total: number };
    },
    refetchOnWindowFocus: false,
    staleTime: 60000,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['servicenow-logs', customerId, page, pageSize, appliedSearch, filterLogType, filterDirection, filterSyncRunId, retryableOnly],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pageSize.toString(),
        ...(appliedSearch && { search: appliedSearch }),
        ...(filterLogType && { logType: filterLogType }),
        ...(filterDirection && { direction: filterDirection }),
        ...(filterSyncRunId && { syncRunId: filterSyncRunId }),
        ...(retryableOnly && { retryableOnly: 'true' }),
        ...(customerId && { customerId }),
      });
      const response = await api.get(`/integrations/servicenow/logs?${params}`);
      return response.data;
    },
    refetchOnWindowFocus: false,
    staleTime: 30000,
  });

  const { data: statsData } = useQuery({
    queryKey: ['servicenow-log-stats', customerId],
    queryFn: async () => {
      const params = customerId ? `?customerId=${encodeURIComponent(customerId)}` : '';
      const response = await api.get(`/integrations/servicenow/logs/stats${params}`);
      return response.data.data as Stats;
    },
    refetchOnWindowFocus: false,
    staleTime: 60000,
  });

  const retryMutation = useMutation({
    mutationFn: (logId: string) => api.post(`/integrations/servicenow/logs/${logId}/retry`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['servicenow-logs'] });
      queryClient.invalidateQueries({ queryKey: ['servicenow-log-stats'] });
    },
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setAppliedSearch(searchInput);
    setPage(1);
  };

  const formatTimeAgo = (date: string) => {
    const now = new Date();
    const then = new Date(date);
    const diff = now.getTime() - then.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  const hasActiveFilters = appliedSearch || filterLogType || filterDirection || filterSyncRunId || retryableOnly;

  const clearAllFilters = () => {
    setSearchInput('');
    setAppliedSearch('');
    setFilterLogType('');
    setFilterDirection('');
    setFilterSyncRunId('');
    setRetryableOnly(false);
    setPage(1);
  };

  if (isLoading) {
    return (
      <div className={embedded ? '' : 'p-4'}>
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4" />
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={embedded ? '' : 'p-4'}>
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">Error loading ServiceNow sync logs. Please try again.</p>
        </div>
      </div>
    );
  }

  const logs: SyncLog[] = data?.data?.logs || [];
  const total = data?.data?.total || 0;
  const totalPages = data?.data?.totalPages || 1;

  return (
    <div className={embedded ? 'flex flex-col min-h-0' : 'p-4 flex flex-col h-full min-h-0 overflow-hidden'}>
      {/* Header */}
      <div className="bg-white shadow rounded-lg mb-3 flex-shrink-0">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div>
              <h1 className={`${embedded ? 'text-lg font-semibold' : 'text-xl font-bold'} text-gray-900`}>
                ServiceNow Sync Logs
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Bi-directional sync activity between ServiceNow and Pulse
                {customerId ? ' for this customer' : ''}
              </p>
            </div>
            {!embedded && (
              <Link
                to="/admin/servicenow-integration"
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                <Cog6ToothIcon className="h-4 w-4 mr-2" />
                Configuration
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Stats Summary */}
      {statsData && (
        <div className="bg-white shadow rounded-lg mb-3 px-4 py-3 flex-shrink-0">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-6 text-sm flex-wrap">
              <span className="flex items-center gap-1.5">
                <span className="text-gray-600">Total Logs:</span>
                <span className="font-semibold text-gray-900">{statsData.totalLogs}</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-red-500" />
                <span className="text-gray-600">Errors:</span>
                <span className="font-semibold text-gray-900">{statsData.errors}</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-amber-500" />
                <span className="text-gray-600">Failed Links:</span>
                <span className="font-semibold text-gray-900">{statsData.failedLinks}</span>
              </span>
            </div>
            {statsData.lastRun && (
              <div className="text-sm text-gray-500">
                Last sync: {formatTimeAgo(statsData.lastRun.startedAt)} ({statsData.lastRun.status})
              </div>
            )}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white shadow rounded-lg mb-3 px-4 py-3 flex-shrink-0">
        <form onSubmit={handleSearch} className="flex items-center gap-3 flex-wrap">
          <select
            value={filterSyncRunId}
            onChange={(e) => {
              setFilterSyncRunId(e.target.value);
              setPage(1);
            }}
            className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
          >
            <option value="">All Runs</option>
            {syncRunsData?.runs?.map((run) => (
              <option key={run.id} value={run.id}>
                {new Date(run.startedAt).toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}{' '}
                - {run.status} ({run._count?.logs || 0} logs)
              </option>
            ))}
          </select>

          <select
            value={filterLogType}
            onChange={(e) => {
              setFilterLogType(e.target.value);
              setPage(1);
            }}
            className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
          >
            <option value="">All Types</option>
            {Object.entries(logTypeConfig).map(([key, cfg]) => (
              <option key={key} value={key}>{cfg.label}</option>
            ))}
          </select>

          <select
            value={filterDirection}
            onChange={(e) => {
              setFilterDirection(e.target.value);
              setPage(1);
            }}
            className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
          >
            <option value="">All Directions</option>
            <option value="INBOUND">Inbound (SN → Pulse)</option>
            <option value="OUTBOUND">Outbound (Pulse → SN)</option>
          </select>

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={retryableOnly}
              onChange={(e) => {
                setRetryableOnly(e.target.checked);
                setPage(1);
              }}
            />
            Retryable only
          </label>

          <div className="flex-1" />

          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search logs..."
              className="pl-9 pr-3 py-2 w-64 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
          >
            Search
          </button>
        </form>
      </div>

      {/* Active Filters */}
      {hasActiveFilters && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex flex-wrap gap-2">
              {filterSyncRunId && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm font-medium border border-indigo-300">
                  Run: {filterSyncRunId.slice(0, 8)}...
                  <button
                    type="button"
                    onClick={() => { setFilterSyncRunId(''); setPage(1); }}
                    className="ml-1 hover:bg-indigo-200 rounded-full p-0.5"
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </span>
              )}
              {appliedSearch && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium border border-blue-300">
                  <MagnifyingGlassIcon className="h-4 w-4" />
                  Search: &quot;{appliedSearch}&quot;
                  <button
                    type="button"
                    onClick={() => { setSearchInput(''); setAppliedSearch(''); setPage(1); }}
                    className="ml-1 hover:bg-blue-200 rounded-full p-0.5"
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </span>
              )}
              {filterLogType && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium border border-purple-300">
                  Type: {logTypeConfig[filterLogType]?.label || filterLogType}
                  <button
                    type="button"
                    onClick={() => { setFilterLogType(''); setPage(1); }}
                    className="ml-1 hover:bg-purple-200 rounded-full p-0.5"
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </span>
              )}
              {filterDirection && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-pink-100 text-pink-800 rounded-full text-sm font-medium border border-pink-300">
                  Direction: {filterDirection}
                  <button
                    type="button"
                    onClick={() => { setFilterDirection(''); setPage(1); }}
                    className="ml-1 hover:bg-pink-200 rounded-full p-0.5"
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </span>
              )}
              {retryableOnly && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm font-medium border border-amber-300">
                  Retryable only
                  <button
                    type="button"
                    onClick={() => { setRetryableOnly(false); setPage(1); }}
                    className="ml-1 hover:bg-amber-200 rounded-full p-0.5"
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={clearAllFilters}
              className="px-3 py-1 text-sm text-blue-700 hover:text-blue-900 hover:bg-blue-100 rounded-md transition-colors"
            >
              Clear All
            </button>
          </div>
        </div>
      )}

      {/* Logs List */}
      <div className="flex-1 min-h-0 overflow-auto">
        <div className="space-y-3">
          {logs.length === 0 ? (
            <div className="bg-white shadow rounded-lg p-8 text-center">
              <ExclamationTriangleIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No logs found</p>
            </div>
          ) : (
            logs.map((log) => {
              const config = logTypeConfig[log.logType] || {
                label: log.logType,
                color: 'bg-gray-100 text-gray-800 border-gray-300',
                icon: ExclamationTriangleIcon,
                iconColor: 'text-gray-600',
                borderColor: '#9ca3af',
              };
              const IconComponent = config.icon;

              return (
                <div
                  key={log.id}
                  className="bg-white shadow rounded-lg p-4 border-l-4"
                  style={{ borderLeftColor: config.borderColor }}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <div className={`p-2 rounded-lg ${config.color}`}>
                        <IconComponent className={`h-5 w-5 ${config.iconColor}`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.color} border`}>
                            {config.label}
                          </span>
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              log.direction === 'INBOUND'
                                ? 'bg-cyan-50 text-cyan-800 border border-cyan-200'
                                : 'bg-orange-50 text-orange-800 border border-orange-200'
                            }`}
                          >
                            {log.direction === 'INBOUND' ? 'SN → Pulse' : 'Pulse → SN'}
                          </span>
                          <span className="text-sm text-gray-500">
                            {formatDateTime(log.timestamp)}
                          </span>
                        </div>
                        <div className="space-y-1">
                          {log.externalNumber && (
                            <div className="text-sm">
                              <span className="text-gray-500">ServiceNow:</span>{' '}
                              <span className="font-medium text-gray-900">{log.externalNumber}</span>
                              {log.externalTable && (
                                <span className="text-gray-500 ml-1">({log.externalTable})</span>
                              )}
                            </div>
                          )}
                          {log.caseNumber && (
                            <div className="text-sm">
                              <span className="text-gray-500">Pulse Ticket:</span>{' '}
                              <span className="font-medium text-gray-900">{log.caseNumber}</span>
                            </div>
                          )}
                        </div>
                        <div className="mt-2">
                          <div className="text-sm text-gray-700">
                            <span className="font-medium">Reason:</span> {log.reason}
                          </div>
                          {log.errorDetail && (
                            <div className="text-sm text-red-600 mt-1 flex items-start gap-1">
                              <ExclamationCircleIcon className="h-4 w-4 shrink-0 mt-0.5" />
                              {log.errorDetail}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    {log.retryable && !log.retriedAt && (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-indigo-700 bg-indigo-50 rounded-md hover:bg-indigo-100 shrink-0"
                        disabled={retryMutation.isPending}
                        onClick={() => retryMutation.mutate(log.id)}
                      >
                        <ArrowPathIcon className="h-4 w-4" />
                        Retry
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Pagination */}
      {logs.length > 0 && (
        <div className="mt-3 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white px-4 py-3 rounded-lg shadow flex-shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">Show</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
            <span className="text-sm text-gray-700">entries</span>
          </div>

          <div className="text-sm text-gray-700">
            Showing {Math.min((page - 1) * pageSize + 1, total)} to{' '}
            {Math.min(page * pageSize, total)} of {total} entries
          </div>

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage(1)}
              disabled={page === 1}
              className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              &laquo;
            </button>
            <button
              type="button"
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            {(() => {
              const pages: (number | string)[] = [];
              if (totalPages <= 5) {
                for (let i = 1; i <= totalPages; i++) pages.push(i);
              } else if (page <= 3) {
                pages.push(1, 2, 3, '...', totalPages);
              } else if (page >= totalPages - 2) {
                pages.push(1, '...', totalPages - 2, totalPages - 1, totalPages);
              } else {
                pages.push(1, '...', page - 1, page, page + 1, '...', totalPages);
              }
              return pages.map((p, idx) =>
                p === '...' ? (
                  <span key={`ellipsis-${idx}`} className="px-2 text-sm text-gray-500">...</span>
                ) : (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPage(p as number)}
                    className={`px-3 py-1 text-sm border rounded-md ${
                      page === p
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {p}
                  </button>
                )
              );
            })()}
            <button
              type="button"
              onClick={() => setPage(page + 1)}
              disabled={page >= totalPages}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
            <button
              type="button"
              onClick={() => setPage(totalPages)}
              disabled={page >= totalPages}
              className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              &raquo;
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceNowSyncLogList;
